import { Stack, Typography, Link as MuiLink } from "@mui/material";
import { MarketoLabel } from "../components/marketo/MarketoLabel";
import marketoAdmin from "../assets/images/marketo_admin.webp";
import marketoMunchkin from "../assets/images/marketo_munchkin.webp";
import marketoInstalledServices from "../assets/images/marketo_installed_services.webp";
import marketoDetails from "../assets/images/marketo_details.webp";
import marketoNewService from "../assets/images/marketo_new_service.webp";
import { Link } from "react-router-dom";

const munchkinList = [
  "Open Marketo and go to Admin in the top navigation.",
  "Click Munchkin in the left sidebar.",
  "Copy the Account ID — format: 123-ABC-456",
];

const clientList = [
  "Go to Admin → LaunchPoint.",
  "Find your API service in the list and click View Details.",
  "Copy your Client ID and Client Secret from the dialog.",
];

const ImageBlock = ({ src, alt }: { src: string; alt: string }) => {
  return (
    <Stack
      sx={{
        overflow: "hidden",
      }}
    >
      <img
        src={src}
        alt={alt}
        style={{
          width: "100%",
          display: "block",
        }}
      />
    </Stack>
  );
};

const LoComponent = ({ text, num }: { text: string; num: number }) => {
  return (
    <Stack flexDirection={"row"} alignItems={"center"} gap={"8px"}>
      <span
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "24px",
          height: "24px",
          backgroundColor: "#0860A8",
          borderRadius: "50%",
          fontWeight: 400,
          fontSize: "12px",
          lineHeight: "24px",
          color: "white",
        }}
      >
        {num}
      </span>
      <Typography variant={"button"}>{text}</Typography>
    </Stack>
  );
};

const LiComponent = ({ text }: { text: string }) => {
  return (
    <Stack flexDirection={"row"} alignItems={"center"} gap={"8px"}>
      <span
        style={{
          width: "6px",
          height: "6px",
          backgroundColor: "#0860A8",
          borderRadius: "50%",
        }}
      />
      <Typography
        variant={"button"}
        sx={{ color: "#464646", lineHeight: "24px" }}
      >
        {text}
      </Typography>
    </Stack>
  );
};

const MarketoGuidePage = () => {
  return (
    <Stack gap={"32px"} maxWidth={"736px"} alignSelf={"center"}>
      <Stack gap={"8px"}>
        <Stack flexDirection={"row"} gap={"12px"}>
          <span
            style={{
              backgroundColor: "#E8ECF0",
              borderRadius: "6px",
              fontWeight: 500,
              fontSize: "13px",
              lineHeight: "12px",
              padding: "10px 12px",
            }}
          >
            Setup guide
          </span>
          <MarketoLabel />
        </Stack>
        <Typography variant="h1" sx={{ color: "#232323" }}>
          How To Find Your Marketo Credentials
        </Typography>
        <Stack gap={"24px"}>
          <Typography variant="subtitle1" sx={{ color: "#464646" }}>
            To connect your Marketo account with the INFUSE Buying Committee
            Radar, you require:
          </Typography>
          <Stack>
            <Typography variant="subtitle1" sx={{ color: "#464646" }}>
              - Marketo Admin Access (
              <Link
                to="https://experienceleague.adobe.com/en/docs/marketo/using/product-docs/administration/marketo-with-adobe-identity/admin-setup"
                style={{ color: "#0069C4" }}
                target="_blank"
              >
                <span>learn more about admin access here</span>
              </Link>
              )
            </Typography>
            <Typography variant="subtitle1" sx={{ color: "#464646" }}>
              - Three values (Munchkin ID, Client ID, and Client Secret)
            </Typography>
          </Stack>
          <Typography variant="subtitle1" sx={{ color: "#464646" }}>
            Once you have confirmed Marketo Admin Access, read below the
            step-by-step to acquire each of the three values required for the
            setup:
          </Typography>
        </Stack>
      </Stack>
      <Stack gap={"24px"}>
        <LoComponent text={"Munchkin ID"} num={1} />
        <Stack gap={"12px"}>
          {munchkinList.map((el, idx) => (
            <LiComponent key={idx} text={el} />
          ))}
        </Stack>
        <ImageBlock src={marketoAdmin} alt={"Admin Tab Image"} />
        <ImageBlock src={marketoMunchkin} alt={"Munchkin Tab Image"} />
      </Stack>

      <Stack gap={"24px"}>
        <LoComponent text={"Client ID & Client Secret"} num={2} />
        <Stack gap={"12px"}>
          <LiComponent text={clientList[0]} />
          <Stack flexDirection={"row"} alignItems={"center"} gap={"8px"}>
            <span
              style={{
                width: "6px",
                height: "6px",
                backgroundColor: "#0860A8",
                borderRadius: "50%",
              }}
            />
            <Typography
              variant={"button"}
              sx={{ color: "#464646", lineHeight: "24px" }}
            >
              Find your API service in the list and click{" "}
              <strong>View Details</strong>.
            </Typography>
          </Stack>
          <LiComponent text={clientList[2]} />
        </Stack>
        <ImageBlock
          src={marketoInstalledServices}
          alt={"Installed Services Tab Image"}
        />
        <ImageBlock src={marketoDetails} alt={"Details Image"} />

        <Stack
          sx={{
            border: "1px solid #FFD5A1",
            backgroundColor: "#FFF7E5",
            borderRadius: "16px",
            padding: "16px",
            gap: "8px",
          }}
        >
          <Typography
            sx={{
              color: "#232323",
              lineHeight: "19px",
              fontSize: "14px",
              fontWeight: 600,
            }}
          >
            No Service in LaunchPoint?
          </Typography>
          <Stack paddingLeft={"8px"}>
            <Stack flexDirection={"row"} gap={"8px"} alignItems={"center"}>
              <span
                style={{
                  width: "5px",
                  height: "5px",
                  backgroundColor: "#232323",
                  borderRadius: "50%",
                }}
              />
              <Typography
                variant={"body1"}
                sx={{
                  color: "#232323",
                }}
              >
                Click <span style={{ fontWeight: 600 }}>New Service</span>.
              </Typography>
            </Stack>
            <Stack flexDirection={"row"} gap={"8px"} alignItems={"center"}>
              <span
                style={{
                  width: "5px",
                  height: "5px",
                  backgroundColor: "#232323",
                  borderRadius: "50%",
                }}
              />
              <Typography
                variant={"body1"}
                sx={{
                  color: "#232323",
                }}
              >
                Select <span style={{ fontWeight: 600 }}>Custom</span> as the
                type, fill in a name, and save.
              </Typography>
            </Stack>
            <Stack flexDirection={"row"} gap={"8px"} alignItems={"center"}>
              <span
                style={{
                  width: "5px",
                  height: "5px",
                  backgroundColor: "#232323",
                  borderRadius: "50%",
                }}
              />
              <Typography
                variant={"body1"}
                sx={{
                  color: "#232323",
                }}
              >
                Then click <span style={{ fontWeight: 600 }}>View Details</span>{" "}
                to get your credentials.
              </Typography>
            </Stack>
          </Stack>
        </Stack>
        <ImageBlock src={marketoNewService} alt={"New Service Image"} />

        <Typography
          sx={{
            fontFamily: "Inter",
            fontWeight: 400,
            fontSize: "14px",
            lineHeight: "22px",
            letterSpacing: "0.3px",
            color: "#797979",
            mt: "16px",
            textAlign: "center",
          }}
        >
          <MuiLink
            href="https://infuse.com/terms-and-conditions/"
            target="_blank"
            rel="noopener noreferrer"
            sx={{ color: "#2194D2", textDecorationColor: "#2194D2" }}
          >
            Terms of Use
          </MuiLink>{" "}
          |{" "}
          <MuiLink
            href="https://infuse.com/privacy-policy/"
            target="_blank"
            rel="noopener noreferrer"
            sx={{ color: "#2194D2", textDecorationColor: "#2194D2" }}
          >
            Privacy Policy
          </MuiLink>
        </Typography>
      </Stack>
    </Stack>
  );
};

export default MarketoGuidePage;
